import { setMockPianoConfig } from 'services/Piano';
import type { GetStaticPaths, GetStaticProps } from 'next';
import getFooter from 'api/Footer/getFooter';
import setCryptoPageAdConfig from 'services/Ad/config/setCryptoPageAdConfig';
import type { HeaderNav } from 'interfaces/navigation/Header';
import type { TermPage } from 'interfaces/content/TermPage';
import TermNextPage from 'pages/CrashCourseTermPage/TermNextPage';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { type HeadData, setHeadData } from 'utils/metaDataUtils/headData';
import getCrashCourseTerm from 'api/CrashCourse/getCrashCourseTerm';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import RightRailFlex from 'components/Ad/AdSlot/RightRailFlex';
import getCrashCoursesArticles from 'api/CrashCourse/getAllCrashCoursesArticles';
import { getMicrositeConfig, addSocialImagesBackup } from 'api/MicrositeConfig/getMicrositeConfig';
import getMenu from 'api/Header/getMenu';
import Image from 'next/image';
import { Microsites } from 'constants/constants';
import { parseCaptionWhenAltTextEmpty } from 'utils/parsingUtils/clientSideParsing';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';

interface CrashCourseTermProps {
  term: TermPage;
}

const CrashCourseTerm = ({ term }: CrashCourseTermProps) => (
  <TermNextPage
    term={term}
    sidebar={[
      <RightRailFlex
        id={0}
        sticky
        key='right-rail-flex'
      />,
    ]}
    adFrequency={5}
    embeds={
      term.image
        ? [
            {
              component: (
                <Image
                  className='cc-term-image'
                  src={term.image as string}
                  alt={parseCaptionWhenAltTextEmpty(term.image)}
                  width='1200'
                  height='600'
                  key='TermImage'
                />
              ),
              index: 2,
              node: 'p',
            },
          ]
        : []
    }
  />
);

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', Microsites.Crypto, null),
      fallbackData.headerNav,
    );

    const { slug } = context.params as { slug: string };
    const term = await getCrashCourseTerm(slug);
    const generalConfig = (await safeAsync(() => getMicrositeConfig(Microsites.Crypto), fallbackData.pageData)).general;
    const dataLayer = safe(() => setPageGtmConfig(term), fallbackData.dataLayer);
    const headData: HeadData = safe(
      () => setHeadData(addSocialImagesBackup(term, generalConfig), true, 'Fortune Crypto'),
      fallbackData.headData,
    );
    const pianoPageConfig = setMockPianoConfig();
    const pageAdConfig = safe(() => setCryptoPageAdConfig(term), fallbackData.pageAdConfig);

    return {
      props: {
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        pageAdConfig,
        pianoPageConfig,
        term,
      },
      revalidate: 60, // In seconds
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const terms = await getCrashCoursesArticles(1);
  const paths = terms.map((term) => ({
    params: {
      slug: term.slug,
    },
  }));

  return {
    fallback: 'blocking',
    paths,
  };
};

export default CrashCourseTerm;
